<template>
  <div class="hotsite-person-type-fields">
    <v-row>
      <v-col class="pa-0 pl-3">
        <v-checkbox v-model="isEnabled" :label="`Permitir cadastro de ${personTypeLabel.toLowerCase()}`" />
      </v-col>
      <v-col class="pa-0 pr-3" cols="auto">
        <mf-button outlined small label="Restaurar campos padrão" @click="restoreDefaults" />
      </v-col>
    </v-row>

    <v-row v-for="(row, rowIndex) of rows" :key="`row-${rowIndex}`">
      <v-col v-for="(field, fieldIndex) of row" :key="`col-${rowIndex}-${fieldIndex}`" :style="{ maxWidth: `${field.width || 100}%` }">
        <hotsite-edit-field
          :field="field"
          :is-first-field="rowIndex === 0 && fieldIndex === 0"
          :is-last-field="rowIndex === rows.length - 1 && fieldIndex === row.length - 1"
          @update:field="updateField"
          @move-up="moveField('up', $event)"
          @move-down="moveField('down', $event)"
          @remove="removeField"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'HotsitePersonTypeFields',
  components: {
    HotsiteEditField: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteEditField.vue'),
  },
  props: {
    personType: {
      type: String,
      required: true,
      validator: (value) => ['N', 'L'].includes(value),
    },
    personTypeLabel: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    lazyFields: [],
  }),
  computed: {
    ...mapGetters('hotsiteConfiguration', ['fieldsWithDefaults', 'defaultConfigurationSettings']),
    fieldKey() {
      return this.personType === 'N' ? 'natural' : 'legal'
    },
    internalFields: {
      get() {
        return this.lazyFields
      },
      set(value) {
        this.lazyFields = value
        this.updatePersonTypeFields(value)
      },
    },
    isEnabled: {
      get() {
        const allowedTypes = this.fieldsWithDefaults?.allowed_person_types_signup || []
        return allowedTypes.includes(this.personType)
      },
      set(value) {
        this.updateAllowedPersonTypes(value)
      },
    },
    rows() {
      return this.internalFields.reduce((rows, field, index) => {
        let lastRowWidth
        field.index = index
        if (rows.length === 0) {
          rows.push([field])
          return rows
        } else {
          lastRowWidth = rows[rows.length - 1].reduce((width, field) => width + (field.width || 100), 0)
        }
        const currentFieldWidth = field.width || 100
        if (lastRowWidth + currentFieldWidth <= 100) {
          rows[rows.length - 1].push(field)
        } else {
          rows.push([field])
        }
        return rows
      }, [])
    },
  },
  watch: {
    fieldsWithDefaults: {
      handler(value) {
        if (value && this.fieldKey) {
          this.lazyFields = value[this.fieldKey] || []
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    if (this.fieldsWithDefaults && this.fieldKey) {
      this.lazyFields = this.fieldsWithDefaults[this.fieldKey] || []
    }
  },
  methods: {
    ...mapActions('hotsiteConfiguration', ['updateFields']),
    updateField(updatedField) {
      const fields = cloneDeep(this.internalFields)
      const fieldIndex = fields.findIndex((f) => f.name === updatedField.name)
      if (fieldIndex !== -1) {
        fields[fieldIndex] = updatedField
        this.internalFields = fields
      }
    },
    removeField(field) {
      const fields = cloneDeep(this.internalFields)
      fields.splice(field.index, 1)
      this.internalFields = fields
    },
    moveField(direction, field) {
      const fields = cloneDeep(this.internalFields)
      if (direction === 'up') {
        fields[field.index] = fields[field.index - 1]
        fields[field.index - 1] = field
      } else if (direction === 'down') {
        fields[field.index] = fields[field.index + 1]
        fields[field.index + 1] = field
      }
      this.internalFields = fields
    },
    updateAllowedPersonTypes(value) {
      const currentTypes = this.fieldsWithDefaults?.allowed_person_types_signup || []
      let newTypes = [...currentTypes]

      if (value && !newTypes.includes(this.personType)) {
        newTypes.push(this.personType)
      } else if (!value && newTypes.includes(this.personType)) {
        newTypes = newTypes.filter((type) => type !== this.personType)
      }

      this.updateFields({
        allowed_person_types_signup: newTypes,
      })
    },
    updatePersonTypeFields(value) {
      this.updateFields({
        [this.fieldKey]: value,
      })
    },
    async restoreDefaults() {
      const confirmed = await this.$confirm({
        title: 'Restaurar padrões',
        message: `Tem certeza que deseja retornar os padrões para os campos de cadastro de pessoa ${this.personTypeLabel.toLowerCase()}?`,
        confirmText: 'Sim',
        cancelText: 'Não',
      })
        .then(() => true)
        .catch(() => false)

      if (confirmed) {
        const defaultFields = cloneDeep(this.defaultConfigurationSettings.fields[this.fieldKey] || [])
        this.internalFields = defaultFields
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.hotsite-person-type-fields {
  .section-title {
    font-size: 20px;
    font-weight: bold;
  }
  .drag-icon {
    position: absolute;
    left: 5px;
    top: 6px;
    color: $cinza-l70;
  }
}
</style>
